<template>
  <div class="asc-plot-time-chart">
    <template v-if="!miniPlot && dateRangeEnabled">
      <div class="asc-plot-time-chart--date-range">
        <asc-label size="md" value="Date Range:" />
        <asc-zoom-chips
          :active-chip="getActiveDateChip"
          :chip-list="dateChips"
          :on-select-chip="handleChipSelection"
        />
      </div>
    </template>
    <template v-if="!isEmpty">
      <asc-stock-chart :options="getUpdatedChartOptions"></asc-stock-chart>
    </template>
    <asc-no-data v-if="isEmpty" :full-size="isFullSizeNoData"></asc-no-data>
    <template v-if="!miniPlot">
      <asc-filter-legend
        class="asc-plot-time-chart--legend"
        :items="getLegendItems"
        :on-item-click="handleLegendItemClick"
        :items-selected="getSelectedLegendItems"
      ></asc-filter-legend>
    </template>
  </div>
</template>

<script>
import AscLabel from '@/components/atoms/AscLabel'
import AscNoData from '@/components/atoms/AscNoData'
import AscFilterLegend from '@/components/molecules/AscFilterLegend'
import AscZoomChips from '@/components/molecules/AscZoomChips'
import variables from '@/styles/_varsJs.scss'
import { PLOT_UPDATE_MESSAGES, ZOOM_CHIPS } from '@/utilities/appConsts'
import { everyNth } from '@/utilities/array'
import { getFormattedUTCDateTime, getUTCDateTime } from '@/utilities/dates'
import {
  getThresholdsGivenPlotTypeId,
  getYAxisMaxValueGivenPlotTypeId,
  getYAxisMinValueGivenPlotTypeId,
  getYAxisTickIntervalGivenPlotTypeId,
} from '@/utilities/plotting'
import _ from 'lodash'
import moment from 'moment'
import AscStockChart from './AscStockChart'

export default {
  name: 'AscPlotTimeChart',

  components: {
    AscStockChart,
    AscNoData,
    AscFilterLegend,
    AscLabel,
    AscZoomChips,
  },

  props: {
    pagePlot: {
      type: Object,
      default: () => {},
    },
    chartIndex: {
      type: Number,
      required: true,
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    multipleCharts: {
      type: Boolean,
      default: false,
    },
    miniPlot: {
      type: Boolean,
      default: false,
    },
    mxRecPlot: {
      type: Boolean,
      default: false,
    },
    reportsPlot: {
      type: Boolean,
      default: false,
    },
    onUpdateTabViewPagePlot: {
      type: Function,
      default: () => {},
    },
    onApplyPagePlot: {
      type: Function,
      default: () => {},
    },
    plotEditor: {
      type: Boolean,
      default: false,
    },
    hideHiddenTabs: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const _that = this

    return {
      dateChips: ZOOM_CHIPS,
      currentPagePlot: Object.assign({}, this.pagePlot),
      chartOptions: null,
      zoomBtnDisplayCount: 5,
      defaultOptions: {
        chart: {
          height: this.miniPlot ? 300 : null,
          marginRight: 30,
          animation: {
            duration: 1000,
          },
          zoomType: 'xy',
          pankey: 'ctrl',
          panning: true,
          resetZoomButton: {
            position: {
              x: 0,
              y: _that.miniPlot || _that.plotEditor ? -10 : -38,
            },
          },
        },
        plotOptions: {
          series: {
            showInNavigator: true,
            gapSize: 1,
          },
        },
        rangeSelector: {
          enabled: false,
          allButtonsEnabled: false,
          buttonSpacing: 4,
          buttonTheme: {
            fill: 'white',
            r: 1,
            style: {
              color: 'black',
            },
            states: {
              hover: {
                fill: variables.accent0,
              },
              select: {
                fill: variables.accent200,
              },
              disabled: {
                fill: variables.gray600,
                style: {
                  cursor: 'not-allowed',
                  color: variables.gray300,
                },
              },
            },
          },
          labelStyle: {
            color: 'black',
            fontWeight: 'bold',
          },
          inputEnabled: false,
        },
        series: [],
        time: {
          useUTC: true,
        },
        title: {
          text: '',
        },
        tooltip: {
          shared: true,
          useHTML: true,
          formatter: function () {
            if (this.points) {
              return (
                `<ul style="list-style: inside; margin:0;padding:0;${
                  this.points.length === 1 ? 'list-style:none' : ''
                } ">` +
                this.points.reduce(function (s, point) {
                  return (
                    s +
                    '<li><b>' +
                    point.series.name +
                    ':</b> ' +
                    point.y.toFixed(2) +
                    `, ${_that.getFlightMappingsFlightName(
                      point,
                      getFormattedUTCDateTime(point.x),
                    )} </li>`
                  )
                }, getFormattedUTCDateTime(this.x)) +
                '</ul>'
              )
            }
            if (!this.points) {
              const formattedDate = getFormattedUTCDateTime(this.x)
              return (
                formattedDate +
                '<br/>' +
                this.series.name +
                ': ' +
                this.y.toFixed(2) +
                `<br/>${_that.getRawDataFlightName(
                  this.series.name,
                  this.x,
                  this.y,
                )}` +
                _that.getAnalyticVersionNumber(this.series.name, this.x, this.y)
              )
            }
          },
        },
      },
    }
  },

  computed: {
    isFullSizeNoData() {
      return this.mxRecPlot || this.reportsPlot
    },

    getUpdatedChartOptions() {
      if (this.chartOptions) {
        return this.chartOptions
      }
      this.updateChartOptions(this.getChartOptions)

      return this.chartOptions
    },

    dateRangeEnabled() {
      if (this.currentPagePlot) {
        return this.currentPagePlot.options.dateRange
      }
      return false
    },

    getActiveDateChip() {
      if (this.currentPagePlot && this.dateRangeEnabled) {
        return this.currentPagePlot.options.dateChip
      }
      return 0
    },

    isEmpty() {
      if (this.getChartData.length > 0) {
        const count = this.getChartData
          .filter(item => !item.tabHidden)
          .reduce((total, item) => {
            const newTotal = total + item.data.length
            return newTotal
          }, 0)
        return count === 0
      }
      return true
    },

    getChartData() {
      if (this.currentPagePlot && this.chartData) {
        const items = this.chartData.filter(
          item =>
            item.plotId === this.currentPagePlot.id &&
            item.chartIndex === this.chartIndex &&
            !item.tabHidden,
        )
        return items || []
      }
      return []
    },

    getSelectedLegendItems() {
      const selectedItems = this.chartData
        .filter(item => item.tabHidden)
        .map(item => item.seriesTitle)
      return selectedItems
    },

    getLegendItems() {
      const chartData = this.hideHiddenTabs
        ? this.chartData.filter(item => !item.tabHidden)
        : this.chartData

      const legendItems = chartData
        .filter(item => item.plotId === this.currentPagePlot.id)
        .sort((a, b) => a.tabIndex - b.tabIndex)
        .map((item, index) => {
          return {
            id: `${item.seriesTitle}-${index}`,
            text: item.seriesTitle,
            styleColor: item.iconColor,
          }
        })

      return [
        {
          header: '',
          items: legendItems,
        },
      ]
    },

    getChartOptions() {
      const xAxisOptions = this.buildXAxisOptions
      const yAxisOptions = this.buildYAxisOptions
      const navigator = this.buildNavigatorOptions
      const series = this.buildSeries
      const zoomButtons =
        this.miniPlot || this.dateRangeEnabled ? [] : this.buildZoomButtons
      const legend = this.buildLegend
      const boost = this.buildBoost

      return {
        ..._.merge(this.defaultOptions, zoomButtons),
        ...series,
        ...xAxisOptions,
        ...yAxisOptions,
        ...navigator,
        ...legend,
        ...boost,
      }
    },

    buildLegend() {
      if (this.miniPlot && this.currentPagePlot.options.hasLegend) {
        return {
          legend: {
            enabled: true,
            align: 'left',
            symbolPadding: 2,
            itemStyle: {
              fontWeight: 'normal',
            },
          },
        }
      }
      return {
        legend: {
          enabled: false,
        },
      }
    },

    buildBoost() {
      if (this.currentPagePlot.options.useBoost && this.miniPlot) {
        return {
          boost: {
            useGPUTranslations: true,
            seriesThreshold: 5,
            enabled: true,
          },
        }
      }
      return {
        boost: {
          enabled: false,
        },
      }
    },

    buildZoomButtons() {
      const buttons = []

      // Pushing X-axis min max
      this.buildSeries.series.forEach((series, index) => {
        const allMin = []
        const allMax = []

        if (series.custom.dataMin && series.custom.dataMax) {
          allMin.push(series.custom.dataMin[0])
          allMax.push(series.custom.dataMax[0])
        }

        const dataMax = _.min(allMax)
        const dataMin = _.max(allMin)

        if (dataMax && dataMin) {
          const diff = moment(dataMax).diff(moment(dataMin), 'hours', true)

          // Less than a day
          if (diff <= 24)
            buttons[index] = this.getZoomButtons(
              {
                hour: _.concat(
                  _.range(1, diff, Math.round(diff / this.zoomBtnDisplayCount)),
                  'all',
                ),
              },
              this,
            )
          // Less than a week to month
          else if (diff <= 730) {
            const days = moment(dataMax).diff(moment(dataMin), 'days', true)
            buttons[index] = this.getZoomButtons(
              {
                day: _.concat(
                  _.range(1, days, Math.round(days / this.zoomBtnDisplayCount)),
                  'all',
                ),
              },
              this,
            )
          }
          // Less than a year
          else if (diff <= 8760) {
            const months = moment(dataMax).diff(moment(dataMin), 'months', true)
            buttons[index] = this.getZoomButtons(
              {
                month: _.range(1, months, Math.round(months / 4)),
                year: ['ytd', 1, 'all'],
              },
              this,
            )
          }
          // Greater than a decade
          else if (diff <= 87600) {
            const years = moment(dataMax).diff(moment(dataMin), 'years', true)
            buttons[index] = this.getZoomButtons(
              {
                year: _.concat(
                  _.range(1, years, Math.round(years / 4)),
                  'ytd',
                  'all',
                ),
              },
              this,
            )
          }
          // Default more than a decade
          else
            buttons[index] = this.getZoomButtons(
              {
                month: [1, 3, 6],
                year: ['ytd', 1, 'all'],
              },
              this,
            )
        }
      })

      const order = ['hour', 'day', 'month', 'year', 'ytd', 'all']

      const aggregatedZoomButtons =
        this.buildSeries.series && this.buildSeries.series.length === 1
          ? buttons[0]
          : _.sortBy(_.intersectionBy(...buttons, 'text'), item =>
              order.indexOf(item.type),
            ).map((item, index) => {
              return { ...item, index: index }
            })

      return {
        rangeSelector: {
          enabled: !!aggregatedZoomButtons?.length || false,
          buttons: aggregatedZoomButtons || [],
          selected: this.currentPagePlot.options.axes?.savedZoom?.index,
        },
      }
    },

    buildXAxisOptions() {
      const plotLines = []

      const xAxisUnits = this.getXAxisLabel(this.getChartData)

      return {
        xAxis: {
          type: 'datetime',
          crosshair: {
            width: 2,
            color: 'gray',
            dashStyle: 'shortdot',
          },
          plotLines,
          title: {
            text: xAxisUnits,
            style: {
              fontWeight: 'bold',
            },
          },
        },
      }
    },

    buildNavigatorOptions() {
      const navigatorOptions = {
        navigator: {
          enabled: !this.miniPlot,
          adaptToUpdatedData: false,
          series: [],
          xAxis: {
            visible: false,
          },
          yAxis: {
            visible: false,
          },
        },
        scrollbar: { enabled: !this.miniPlot, liveRedraw: false },
      }

      return navigatorOptions
    },

    buildYAxisOptions() {
      const _that = this
      const filteredData = _.filter(this.getChartData, function (item) {
        return !item.tabHidden
      })
      const filteredUnits = this.getUnits(filteredData)
      const units = this.getUnits(this.getChartData)
        .filter(item => !item.tabHidden)
        .map(item => {
          let baseProps = {}
          if (item.toLowerCase().includes('score')) {
            baseProps = {
              min: this.getYAxisMinValue,
              max: this.getYAxisMaxValue,
            }
          }
          return {
            ...baseProps,
            opposite: false,
            title: {
              text: filteredUnits.includes(item) ? item : null,
              style: {
                fontWeight:
                  this.currentPagePlot.plotTypeId === 8068 ? 'bold' : 'normal',
              },
            },
            tickInterval:
              item === 'Signal'
                ? 1
                : item.toLowerCase().includes('score')
                ? this.getYAxisTickInterval
                : null,
            alignTicks: item !== 'Signal',
            gridLineWidth: item === 'Signal' ? 0 : 1,
            allowDecimals: item !== 'Signal',
            labels: {
              formatter: function () {
                let s = this.value
                const unitKeys = _that.getKeys(item)
                if (unitKeys) {
                  s += ` (${unitKeys[this.value]})`
                }
                return s
              },
            },
            plotLines: item.toLowerCase().includes('score')
              ? this.buildThresholdPlotLines
              : [],
            showEmpty: false,
          }
        })
      const yAxisOptions = {
        yAxis: units,
      }

      return yAxisOptions
    },

    getYAxisTickInterval() {
      return getYAxisTickIntervalGivenPlotTypeId(
        this.currentPagePlot.plotTypeId,
      )
    },

    getYAxisMaxValue() {
      if (this.currentPagePlot.options.axes.autoscale) {
        return null
      }

      return getYAxisMaxValueGivenPlotTypeId(this.currentPagePlot.plotTypeId)
    },

    getYAxisMinValue() {
      if (this.currentPagePlot.options.axes.autoscale) {
        return null
      }

      return getYAxisMinValueGivenPlotTypeId(this.currentPagePlot.plotTypeId)
    },

    buildThresholdPlotLines() {
      const thresholds = getThresholdsGivenPlotTypeId(
        this.currentPagePlot.plotTypeId,
      )
      if (thresholds) {
        return thresholds.map(item => {
          return {
            value: item.value,
            color: variables.warningbase,
            dashStyle: 'shortdash',
            width: 2,
          }
        })
      }

      return []
    },

    buildSeries() {
      const series = []

      if (this.currentPagePlot) {
        if (this.multipleCharts) {
          const [tab] = this.currentPagePlot.options.data.tabs.filter(
            item => item.tabIndex === this.chartIndex,
          )
          if (tab) {
            const [first] = _.cloneDeep(
              this.getChartData.filter(
                item =>
                  item.plotId === this.currentPagePlot.id &&
                  item.tabIndex === this.chartIndex &&
                  !item.tabHidden,
              ),
            )

            if (first) {
              const filteredData = everyNth(first.data, 1)

              if (first.keys) {
                Object.keys(first.keys).forEach(item => {
                  const data = first
                    ? filteredData.filter(row => {
                        return row[1] === Number(item)
                      })
                    : []

                  series.push({
                    turboThreshold: 0,
                    name: first ? first.seriesTitle + item : 'N/A',
                    type: tab.chartType,
                    tooltip: {
                      valueDecimals: 0,
                    },
                    data: data,
                    color: tab.iconColor,
                    marker: {
                      symbol: tab.icon,
                      radius: 2,
                    },
                    yAxis: first ? this.getUnitIndex(first.unit) : 0,
                    custom: {
                      dataMin: _.min(data),
                      dataMax: _.max(data),
                    },
                    // showInNavigator: false,
                    visible: !first.tabHidden,
                  })
                })
              } else {
                series.push({
                  turboThreshold: 0,
                  name: first ? first.seriesTitle : 'N/A',
                  type: tab.chartType,
                  tooltip: {
                    valueDecimals: 0,
                  },
                  data: first ? filteredData : [],
                  color: tab.iconColor,
                  marker: {
                    symbol: tab.icon,
                    radius: 2,
                  },
                  yAxis: first ? this.getUnitIndex(first.unit) : 0,
                  custom: {
                    dataMin: first ? _.min(filteredData) : [],
                    dataMax: first ? _.max(filteredData) : [],
                  },
                  // showInNavigator: false,
                  visible: !first.tabHidden,
                })
              }
            }
          }
        }

        if (!this.multipleCharts) {
          this.currentPagePlot.options.data.tabs.forEach((tab, index) => {
            const [first] = _.cloneDeep(
              this.getChartData.filter(
                item =>
                  item.plotId === this.currentPagePlot.id &&
                  item.tabIndex === index &&
                  !item.tabHidden,
              ),
            )

            if (first) {
              const filteredData = everyNth(first.data, 1)

              if (first.keys) {
                Object.keys(first.keys).forEach(item => {
                  const data = first
                    ? filteredData.filter(row => {
                        return row[1] === Number(item)
                      })
                    : []

                  series.push({
                    turboThreshold: 0,
                    name: first ? first.seriesTitle + item : 'N/A',
                    type: tab.chartType,
                    tooltip: {
                      valueDecimals: 0,
                    },
                    data: data,
                    color: tab.iconColor,
                    marker: {
                      symbol: tab.icon,
                      radius: 2,
                    },
                    yAxis: first ? this.getUnitIndex(first.unit) : 0,
                    custom: {
                      dataMin: _.min(data),
                      dataMax: _.max(data),
                    },
                    // showInNavigator: false,
                    visible: !first.tabHidden,
                  })
                })
              } else {
                series.push({
                  turboThreshold: 0,
                  name: first ? first.seriesTitle : 'N/A',
                  type: tab.chartType,
                  tooltip: {
                    valueDecimals: 0,
                  },
                  data: first ? filteredData : [],
                  color: tab.iconColor,
                  marker: {
                    symbol: tab.icon,
                    radius: tab.radius ? tab.radius : 2,
                  },
                  yAxis: first ? this.getUnitIndex(first.unit) : 0,
                  custom: {
                    dataMin: first ? _.min(filteredData) : [],
                    dataMax: first ? _.max(filteredData) : [],
                  },
                  // showInNavigator: false,
                  visible: !first.tabHidden,
                })
              }
            }
          })
        }
      }

      const seriesData = {
        series,
      }

      return seriesData
    },
  },

  watch: {
    pagePlot: {
      deep: true,
      handler(newPagePlot) {
        if (newPagePlot.dataUpdated !== this.currentPagePlot.dataUpdated) {
          this.currentPagePlot = Object.assign({}, newPagePlot)
          this.pagePlot = newPagePlot
          this.updateChartOptions(this.getChartOptions)
        }
      },
    },
  },

  methods: {
    handleChipSelection(data) {
      if (this.onApplyPagePlot) {
        this.onApplyPagePlot(
          this.currentPagePlot.id,
          this.currentPagePlot.plotTypeId,
          PLOT_UPDATE_MESSAGES.updateDateRange,
          data,
        )
      }
    },

    handleLegendItemClick(data) {
      const clickedTab = this.chartData.find(item => item.seriesTitle === data)
      this.onUpdateTabViewPagePlot(
        clickedTab.plotId,
        clickedTab.tabIndex,
        clickedTab.hidden,
      )
    },

    updateChartOptions(cos) {
      this.chartOptions = cos
    },

    getXAxisLabel(data) {
      const groupedTitle = _.groupBy(data, 'xAxisLabel')
      const mappedTitle = Object.keys(groupedTitle).map(item => item)
      return mappedTitle[0] === 'undefined' ? '' : mappedTitle[0]
    },

    getUnits(data) {
      const groupedUnits = _.groupBy(data, 'unit')
      const mappedUnits = Object.keys(groupedUnits).map(item => item)
      return mappedUnits
    },

    getUnitIndex(unit) {
      const index = this.getUnits(this.getChartData).indexOf(unit)
      return index
    },

    getKeys(unit) {
      const [first] = this.getChartData.filter(
        item => item.unit === unit && !item.tabHidden,
      )
      return first ? first.keys : []
    },

    getDepartureDate(firstFlight) {
      const departDate = getUTCDateTime(firstFlight[0].departureDate)
      const subtractDate = departDate.subtract(15, 'm')
      return subtractDate
    },

    getArrivalDate(firstFlight) {
      const departDate = getUTCDateTime(firstFlight[1].departureDate)
      const arriveDate = getUTCDateTime(firstFlight[0].arrivalDate)
      return firstFlight.length > 1 ? departDate : arriveDate
    },

    getDateBetweenTwoDates(date, date1, date2) {
      return date.isBetween(date1, date2)
    },

    getFlightMappingsFlightName(point) {
      const data = this.getChartData
      const [currentTab] = data.filter(
        item => item.seriesTitle === point.series.name && !item.tabHidden,
      )
      if (
        currentTab &&
        currentTab.flightMappings &&
        currentTab.flightMappings.flights?.length > 0
      ) {
        const flights = currentTab.flightMappings.flights
        if (flights && flights.length > 0) {
          const pointDate = moment.utc(+point.x)
          const [filteredFlight] = flights.filter(flight => {
            const departureDate = moment
              .utc(flight.departureDate)
              .add(-15, 'minutes')
            const arrivalDate = moment
              .utc(flight.arrivalDate)
              .add(15, 'minutes')
            return pointDate.isBetween(departureDate, arrivalDate)
          })
          if (filteredFlight) {
            return `<b>Flight :</b> ${filteredFlight.flightNumber}`
          }
        }
        return '<b>Flight :</b> N/A'
      }

      return ''
    },

    getRawDataFlightName(seriesName, x, y) {
      const data = this.getChartData
      const [currentTab] = data.filter(
        item => item.seriesTitle === seriesName && !item.tabHidden,
      )
      const rawData = currentTab.rawData
      const [filtered] = rawData.filter(
        item => item.timestamp === x && item.point === y,
      )

      return filtered && filtered.flightNumber
        ? `Flight: ${filtered.flightNumber}`
        : ''
    },

    getAnalyticVersionNumber(seriesName, x, y) {
      const data = this.getChartData
      const [currentTab] = data.filter(
        item => item.seriesTitle === seriesName && !item.tabHidden,
      )
      const rawData = currentTab.rawData
      const [filtered] = rawData.filter(
        item => item.timestamp === x && item.point === y,
      )
      return filtered && filtered.version
        ? `<br/> Analytic Version Number: ${filtered.version}`
        : ''
    },

    saveZoom(zoomObj, _that) {
      _that.currentPagePlot.options.axes.savedZoom = {
        type: zoomObj.type,
        text: zoomObj.text,
        index: zoomObj.index,
      }

      _that.defaultOptions.rangeSelector.selected = zoomObj.index
    },

    getZoomButtons(time, _that) {
      const buttons = []
      for (const key in time) {
        const interval = time[key]
        interval.forEach(function (range, i) {
          const isRangeNumber = typeof range === 'number'
          buttons.push({
            type: isRangeNumber ? key : range,
            count: isRangeNumber ? range : undefined,
            text: isRangeNumber
              ? range + key.charAt(0).toUpperCase()
              : range === 'ytd'
              ? 'YTD'
              : range === 'all'
              ? 'All'
              : 'N/A',
            index: i,
            events: {
              click() {
                _that.saveZoom(this, _that)
              },
            },
          })
        })
      }

      return buttons
    },
  },
}
</script>

<style lang="scss">
.asc-plot-time-chart {
  .highcharts-button {
    text {
      font-weight: bold !important;
    }
  }

  .highcharts-navigator-mask-outside {
    fill: rgba(0, 0, 0, 0.2) !important;
  }

  .highcharts-navigator-mask-inside {
    fill: rgba(0, 0, 0, 0.06) !important;
  }

  .highcharts-navigator-handle {
    fill: rgb(102, 102, 102) !important;
  }

  .highcharts-reset-zoom {
    opacity: 0.2;
  }

  .highcharts-reset-zoom:hover {
    opacity: 1;
  }

  &--legend {
    padding: 16px;
  }

  &--date-range {
    padding: 15px;

    .asc-zoom-chips {
      background: inherit;
    }

    .asc-zoom-chip {
      flex: inherit;
      min-width: 32px;

      .theme--light.v-btn:not(.v-btn--icon):not(.v-btn--text) {
        height: 24px !important;
        width: 35px !important;
      }

      .v-btn {
        border: 1px solid gray;
        border-radius: 0 !important;
        font-weight: 400 !important;
      }
    }
  }
}

.reset-zoom-btn {
  position: absolute;
  right: 50px;
  top: 5px;
}
</style>
